import { EdgeProps, type Edge } from "@xyflow/react";
import classNames from "classnames";
import React, { memo } from "react";
import { useTreeState } from "./context";
import styles from "./OrgTree.module.scss";

export interface BusEdgeData extends Record<string, unknown> {
    level: number;
    /** расстояние от края родителя до общего ствола */
    bus: number;
}

type BusEdgeType = Edge<BusEdgeData>;

const RADIUS = 9;

/**
 * Ортогональная связь: от карточки-родителя отходит общий ствол, от него — скруглённый
 * отвод к каждому потомку. Отводы всех потомков начинаются в одной точке ствола, поэтому
 * линии не расходятся веером и не пересекаются при любом числе потомков.
 */
export const BusEdge = memo(
    ({ id, source, target, sourceX, sourceY, targetX, targetY, data }: EdgeProps<BusEdgeType>) => {
        const state = useTreeState();
        const level = data?.level ?? 2;
        const bus = sourceX + (data?.bus ?? 26);
        const dy = targetY - sourceY;

        let d: string;
        if (Math.abs(dy) < 1.5) {
            d = `M${sourceX},${sourceY}H${targetX}`;
        } else {
            const s = dy > 0 ? 1 : -1;
            const r = Math.min(RADIUS, Math.abs(dy));
            d = `M${sourceX},${sourceY}H${bus}V${targetY - s * r}Q${bus},${targetY} ${bus + r},${targetY}H${targetX}`;
        }

        const hot = state.path.has(target) && state.path.has(source);
        const dim = !!state.dimmed && state.dimmed.has(target);

        return (
            <path
                id={id}
                d={d}
                className={classNames(styles.wire, {
                    [styles.l0]: level === 0,
                    [styles.l1]: level === 1,
                    [styles.hot]: hot,
                    [styles.dim]: dim && !hot,
                })}
            />
        );
    }
);

export const edgeTypes = { bus: BusEdge };
